import compact from "lodash/compact";
import get from "lodash/get";
import {
  FIELD_ID_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_QUESTIONS_TOGGLE_KEY,
  FIELD_MARKED_FOR_REMOVAL_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FIELD_UNIQUE_ID_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { checkIsDirty } from "PFApp/helpers/activity_form/check_is_dirty";
import { sanitizeFormDataForAPI } from "PFApp/helpers/activity_form/data_conversions";
import { useGetCustomFieldsToSave } from "PFApp/hooks/activity_form/use_custom_fields_to_save";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { MultiActivityForm } from "PFApp/types/activity_form/activity_form_schema";
import { AdminEngagementBlueprintActivitiesAction } from "PFCore/services/admin/engagement_blueprints";
import { ActivityOperation } from "PFCore/services/bulk_operations";
import { useCallback } from "react";
import { FormState } from "react-hook-form";

const isRoleDirty = (
  dirtyFields: FormState<MultiActivityForm>["dirtyFields"],
  subtemplateIndex: number,
  roleIndex: number
) => {
  const roleDirtyFields = get(
    dirtyFields,
    `${FORM_SUBTEMPLATES_KEY}.${subtemplateIndex}.${FORM_ROLES_KEY}.${roleIndex}`
  );

  return checkIsDirty(roleDirtyFields);
};

export const useTransformFormToBulkOperations = () => {
  const { getCustomFieldsToSave } = useGetCustomFieldsToSave();
  const roles = useMultiActivityFormStore((store) => store.roles);

  const transformFormToBulkOperations = useCallback(
    (
      formValues: MultiActivityForm,
      dirtyFields: FormState<MultiActivityForm>["dirtyFields"],
      fieldsBlacklist?: string[],
      dirtyRolesOnly: boolean = true,
      allowUpdate: boolean = true
    ): ActivityOperation[] => {
      const subtemplates = formValues[FORM_SUBTEMPLATES_KEY];

      return subtemplates.flatMap((subtemplate, subtemplateIndex) =>
        compact(
          subtemplate[FORM_ROLES_KEY].map((role, roleIndex) => {
            const itemIdentifier = `${FORM_SUBTEMPLATES_KEY}.${subtemplateIndex}.${FORM_ROLES_KEY}.${roleIndex}`; // eslint-disable-line max-len

            if (dirtyRolesOnly && !isRoleDirty(dirtyFields, subtemplateIndex, roleIndex)) {
              return null;
            }

            const isNew = !role[FIELD_ID_KEY];
            if (!allowUpdate && !isNew) {
              return null;
            }

            if (role[FIELD_MARKED_FOR_REMOVAL_KEY]) {
              return {
                action: AdminEngagementBlueprintActivitiesAction.Destroy,
                itemIdentifier,
                data: { id: role[FIELD_ID_KEY] }
              };
            }

            const sanitizedData = sanitizeFormDataForAPI({
              values: {
                ...role,
                [FIELD_INTEREST_QUESTIONS_TOGGLE_KEY]: !!role[FIELD_INTEREST_QUESTIONS_KEY]
              },
              isNew,
              fieldsBlacklist: [
                ...(fieldsBlacklist || []),
                ...compact([role.subtemplate_key === NO_SUBTEMPLATE_VALUE ? FIELD_SUBTEMPLATE_KEY : null])
              ]
            });

            const activityCreator = roles[role[FIELD_UNIQUE_ID_KEY]]?.activityCreator ?? null;

            const data = {
              ...sanitizedData,
              custom_fields: getCustomFieldsToSave(sanitizedData.custom_fields || [], activityCreator)
            };

            return {
              action: isNew
                ? AdminEngagementBlueprintActivitiesAction.Create
                : AdminEngagementBlueprintActivitiesAction.Update,
              itemIdentifier,
              data
            };
          })
        )
      ) as ActivityOperation[];
    },
    [getCustomFieldsToSave, roles]
  );

  return { transformFormToBulkOperations };
};
